import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {parseSidePanelIssue, writeSidePanelIssueToUrl} from '@github-ui/issues-shared/side-panel-url'
import {useParams} from '@github-ui/react-router'
import {startTransition, useCallback, useEffect, useMemo, useState} from 'react'

type UrlParams = {
  owner: string
  repo: string
  number: string
  viewId: string
  type: string
}

/** The shared contract carries no `type`, which only this stack's identifier needs. */
function parseSidePanelIdentifierFromUrl(): ItemIdentifier | null {
  const issue = parseSidePanelIssue()
  return issue ? ({...issue, type: 'Issue'} as const) : null
}

export const useRouteInfo = () => {
  const {owner, repo, number, viewId} = useParams<UrlParams>()
  const [internalSidePanelIdentifier, setInternalSidePanelIdentifier] = useState<ItemIdentifier | null>(null)

  const setSidePanelItemIdentifier = useCallback((identifier: ItemIdentifier | null) => {
    startTransition(() => {
      setInternalSidePanelIdentifier(identifier)
    })
    writeSidePanelIssueToUrl(identifier)
  }, [])

  // Read the URL post-mount to set initial side panel state.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInternalSidePanelIdentifier(parseSidePanelIdentifierFromUrl())
  }, [])

  const sidePanelItemURL = internalSidePanelIdentifier
    ? `/${internalSidePanelIdentifier.owner}/${internalSidePanelIdentifier.repo}/issues/${internalSidePanelIdentifier.number}`
    : ''

  const itemIdentifier = useMemo(() => {
    let parsedNumber = number ? parseInt(number, 10) : undefined
    if (parsedNumber && parsedNumber < 1) {
      parsedNumber = undefined
    }
    return owner && repo && parsedNumber ? ({owner, repo, number: parsedNumber} as ItemIdentifier) : undefined
  }, [number, owner, repo])

  const onCloseSidePanel = useCallback(() => {
    setSidePanelItemIdentifier(null)
  }, [setSidePanelItemIdentifier])

  const onParentIssueActivate = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, parentIdentifier: ItemIdentifier): boolean => {
      if (!itemIdentifier) return false

      // Only close the side panel if the parent is the same as the current issue
      if (
        parentIdentifier.owner !== itemIdentifier.owner ||
        parentIdentifier.repo !== itemIdentifier.repo ||
        parentIdentifier.number !== itemIdentifier.number
      ) {
        return false
      }

      e.preventDefault()
      onCloseSidePanel()
      return true
    },
    [itemIdentifier, onCloseSidePanel],
  )

  useEffect(() => {
    function onPopState() {
      const identifier = parseSidePanelIdentifierFromUrl()

      if (!identifier) {
        setInternalSidePanelIdentifier(null)
        return
      }

      // Don't open the side panel for the issue that is currently being viewed
      if (
        identifier.owner === itemIdentifier?.owner &&
        identifier.repo === itemIdentifier?.repo &&
        identifier.number === itemIdentifier?.number
      ) {
        return
      }

      setInternalSidePanelIdentifier(identifier)
    }

    window.addEventListener('popstate', onPopState)

    return () => window.removeEventListener('popstate', onPopState)
  }, [itemIdentifier])

  return {
    itemIdentifier,
    viewId,
    sidePanelItemIdentifier: internalSidePanelIdentifier,
    setSidePanelItemIdentifier,
    sidePanelItemURL,
    onCloseSidePanel,
    onParentIssueActivate,
  }
}
